import dayjs from 'dayjs'
import { FEFormData, IBudgetFeeRule, RepastWorkDayEnum, RulePackageRes, SubmitRulePackage, FEExpenseCodeConfig } from './types'
import { BudgetFeeConfigTypeEnum, BudgetFeeTypeEnum, BudgetFeeTypeFeEnum, StaffStatusEnum, transFeeType2Backend, transFeeType2Fe } from '.'
import { getNumStr, isEqualString } from '@/common/utils'
import { generateFormShopLimitConfig } from '../components/ShopLimitSelector'


function getExpenseCodeConfig (resData?: RulePackageRes): FEExpenseCodeConfig {
  // expenseCodeConfig 放在 budgetFeeRule 内返回
  const cfg = resData?.budgetFeeRule?.expenseCodeConfig
  if (!cfg) {
    return { enabled: false, mode: 'NONE', remarkMode: 'NONE' } as FEExpenseCodeConfig
  }
  // 后端字段：switchOn 对应前端 enabled，expenseMode 对应前端 mode
  return {
    enabled: cfg.switchOn,
    mode: (cfg.expenseMode || 'NONE') as FEExpenseCodeConfig['mode'],
    remarkMode: (cfg.remarkMode || 'NONE') as FEExpenseCodeConfig['remarkMode'],
  } as FEExpenseCodeConfig
}


/** 从详情解析表单数据. 不传则代表新建 */
export function getFormdateFromRes (resData?: RulePackageRes): FEFormData {
  const isEdit = resData?.rulePackageId

  const getStaffStatus = () => {
    if (resData?.staffStatusRule?.checkStaffStatus === false) { // 不限制状态
      return StaffStatusEnum.NO_LIMIT
    }
    // 接口有值
    if (resData?.staffStatusRule?.availableStaffStatusCodeList?.length) return String(resData?.staffStatusRule?.availableStaffStatusCodeList[0]) as StaffStatusEnum
    // 默认值
    return StaffStatusEnum.IN_JOB
  }

  const data: FEFormData = {
    rulePackageName: resData?.rulePackageName || '',
    /** 下发方式 */
    budgetFeeType: transFeeType2Fe(resData?.budgetFeeType, resData?.budgetFeeRule?.periodType) as BudgetFeeTypeFeEnum,
    /** 下发周期 */
    periodType: getNumStr(resData?.budgetFeeRule?.periodType),
    /** 下发金额 */
    budgetAmount: resData?.budgetFeeRule?.budgetAmount,
    expiryConfig: {
      expiryType: resData?.budgetFeeRule?.expiryType,
      expiryValue: resData?.budgetFeeRule?.expiryValue,
    },
    initExcelConfig: {
      s3Name: resData?.budgetFeeRule?.initExcelS3Name || '',
      url: resData?.budgetFeeRule?.initExcelUrl || '',
      fileName: resData?.budgetFeeRule?.initExcelName || '',
    },
    amountFrom: getNumStr(resData?.budgetFeeRule?.amountFrom) as BudgetFeeConfigTypeEnum || BudgetFeeConfigTypeEnum.INPUT,
    rulePackageDescription: resData?.rulePackageDescription || '',
    availableServiceCodeList: resData?.userServiceRule?.availableServiceCodeList || ['delivery'],
    disableCategoryCodeList: resData?.merchantCategoryRule?.disableCategoryList?.map(item => item.categoryCode) || [],
    startTime: resData?.effectiveTimeRule?.startTime || dayjs().valueOf(),
    endTime: {
      time: resData?.effectiveTimeRule?.endTime,
      type: resData?.effectiveTimeRule?.endTimeType || 2,
    },
    batchNoList: resData?.balanceBatchInfo?.batchList?.map(item => ({ label: item.batchName, value: item.batchNo })) || [],
    /** 适用人群 */
    assignInfo: {
      assignAll: isEdit ? resData?.assignInfo.assignAll : true, // NOTE: 一定不要给特定兜底值
      assignStaffList: resData?.assignInfo.assignStaffList?.map(item => ({
        label: item.staffName,
        value: item.staffId,
      })) || [],
      assignDepartmentList: resData?.assignInfo.assignDepartmentList?.map(item => ({
        label: item.departmentDisplayName || item.departmentName,
        value: item.departmentId,
      })) || [],
      assignGroupList: resData?.assignInfo.assignGroupList?.map(item => ({
        label: item.groupName,
        value: item.groupId,
      })) || [],
    },
    staffStatus: getStaffStatus(),
    repastPeriodRule: resData?.repastPeriodRule || {
      checkRepastPeriod: false,
      repastPeriodType: 1,

    },
    /** 送餐地址 */
    deliveryAddressRule: resData?.deliveryAddressRule || {
      checkDeliveryAddress: false,
      deliveryAddressList: [],
    },

    /** 自取地址 */
    pickupAddressRule: resData?.pickupAddressRule || {
      checkPickupAddress: false,
      pickupAddressList: [],
    },
    shopLimitConfig: generateFormShopLimitConfig(resData),
    expenseCodeConfig: getExpenseCodeConfig(resData),
  }
  return data
}


/** 从前端表单获取要提交的数据 */
export function getSubmitData (data: FEFormData) {
  const budgetFeeType = transFeeType2Backend(data.budgetFeeType)
  // 构造费用代码配置，字段名与后端保持一致（switchOn/expenseMode/remarkMode）
  const expenseCodeConfig = data.expenseCodeConfig
    ? {
      switchOn: data.expenseCodeConfig.enabled,
      expenseMode: data.expenseCodeConfig.enabled ? (data.expenseCodeConfig.mode || 'NONE') : 'NONE',
      remarkMode: data.expenseCodeConfig.enabled ? (data.expenseCodeConfig.remarkMode || 'NONE') : 'NONE',
    }
    : undefined

  const getFeeRule = () => {
    if (isEqualString(budgetFeeType, BudgetFeeTypeEnum.BATCH)) {
      return {
        /** 余额批次 */
        balanceBatchInfo: { batchNoList: data.batchNoList?.map(item => String(item.value)) || [] },

        /** 适用状态 */
        staffStatusRule: {
          checkStaffStatus: data.staffStatus !== StaffStatusEnum.NO_LIMIT,
          availableStaffStatusCodeList: data.staffStatus === StaffStatusEnum.NO_LIMIT ? [] : [Number(data.staffStatus)],
        },
      }
    } else {
      const budgetFeeRule: IBudgetFeeRule = {
        /** 下发周期 */
        periodType: Number(data.periodType),
        /** 下发金额 */
        budgetAmount: data.budgetAmount || '',
      }
      // 一次性下发, 增加Excel相关字段
      if (isEqualString(data.budgetFeeType, BudgetFeeTypeFeEnum.BUDGET_ONCE)) {
        budgetFeeRule.periodType = 0 // 一次性下发, 周期固定传0
        budgetFeeRule.initExcelS3Name = data.initExcelConfig?.s3Name
        budgetFeeRule.initExcelName = data.initExcelConfig?.fileName
        budgetFeeRule.updateExcelS3Name = data.updateExcelConfig?.s3Name
        budgetFeeRule.updateExcelName = data.updateExcelConfig?.fileName
        budgetFeeRule.amountFrom = data?.amountFrom ? Number(data.amountFrom) : undefined
      } else { // 循环下发
        if (data.expiryConfig) { // 有效期是动态配置,不一定有值
          budgetFeeRule.expiryType = Number(data.expiryConfig?.expiryType)
          budgetFeeRule.expiryValue = data.expiryConfig?.needFillValue ? data.expiryConfig?.expiryValue : ''
        }
      }
      // 费用代码配置放在 budgetFeeRule 内传给后端
      if (expenseCodeConfig) {
        budgetFeeRule.expenseCodeConfig = expenseCodeConfig
      }
      return { budgetFeeRule }
    }
  }

  const repastPeriodRule = { ...(data.repastPeriodRule || {}), repastPeriodList: data.repastPeriodRule?.repastPeriodList?.map(item => ({ ...item,
    weekList: isEqualString(item.repastDateType, RepastWorkDayEnum.WEEK) ? item.weekList : [],
    workdayTypeList: isEqualString(item.repastDateType, RepastWorkDayEnum.WORK_DAY) ? item.workdayTypeList : [],
  })) || [] }

  const result: Omit<SubmitRulePackage, 'budgetFeeRule' | 'staffStatusRule' | 'balanceBatchInfo'> = {
    ...getFeeRule(),
    budgetFeeType: Number(budgetFeeType),
    rulePackageName: data.rulePackageName,
    rulePackageDescription: data.rulePackageDescription,
    userServiceRule: { availableServiceCodeList: data.availableServiceCodeList },

    /** 适用人群 */
    assignInfo: {
      assignAll: data.assignInfo.assignAll, // NOTE: boolean,不要给默认值
      assignStaffIdList: data.assignInfo.assignStaffList.map(item => item.value) || [],
      assignDepartmentIdList: data.assignInfo.assignDepartmentList.map(item => item.value) || [],
      assignGroupIdList: data.assignInfo.assignGroupList?.map(item => item.value) || [],
    },
    merchantCategoryRule: {
      disableCategoryCodeList: data.disableCategoryCodeList,
    },
    effectiveTimeRule: {
      startTime: data.startTime,
      endTime: data.endTime.time,
      endTimeType: !data.endTime.time ? 2 : data.endTime.type, // 未填写时间,则认为长期有效
    },

    repastPeriodRule: repastPeriodRule,
    deliveryAddressRule: data.deliveryAddressRule,
    pickupAddressRule: data.pickupAddressRule || {
      checkPickupAddress: false,
      pickupAddressList: [],
    },
    bannedShopRule: {
      checkBannedShop: Boolean(data.shopLimitConfig.enable),
      bannedShopList: data.shopLimitConfig.shopList.map(item => item.originValue),
    },
    bannedBrandRule: {
      checkBannedBrand: Boolean(data.shopLimitConfig.enable),
      bannedBrandList: data.shopLimitConfig.brandList.map(item => item.originValue),
    },
  }
  return result
}